import { getConfig } from '@edx/frontend-platform';
import { IntlShape } from '@edx/frontend-platform/i18n';
import { ROUTES } from '@src/routes';
import messages from './messages';

type StaticPageLink = {
  label: string;
  to?: string;
  href?: string;
};

type StaticPageSection = {
  title: string;
  paragraphs: string[];
};

type StaticPageAsideCard = {
  title: string;
  description: string;
  links?: StaticPageLink[];
};

export type StaticPageContent = {
  pageTitle: string;
  eyebrow: string;
  title: string;
  lead: string;
  referenceLabel: string;
  referenceValue: string;
  sections: StaticPageSection[];
  asideCards: StaticPageAsideCard[];
};

const getSupportHref = () => getConfig().SUPPORT_URL || `mailto:${getConfig().INFO_EMAIL}`;
const getInfoEmailHref = () => `mailto:${getConfig().INFO_EMAIL}`;

const getCommonAsideCards = (intl: IntlShape): StaticPageAsideCard[] => [
  {
    title: intl.formatMessage(messages.commonQuickLinksTitle),
    description: '',
    links: [
      { label: intl.formatMessage(messages.commonQuickLinksCourses), to: ROUTES.COURSES },
      { label: intl.formatMessage(messages.commonQuickLinksFaq), to: ROUTES.FAQ },
      { label: intl.formatMessage(messages.commonQuickLinksHelp), to: ROUTES.HELP },
    ],
  },
  {
    title: intl.formatMessage(messages.commonContactTitle),
    description: intl.formatMessage(messages.commonContactDescription),
    links: [
      { label: intl.formatMessage(messages.commonContactButton), href: getInfoEmailHref() },
    ],
  },
];

export const getAboutPageContent = (intl: IntlShape): StaticPageContent => ({
  pageTitle: intl.formatMessage(messages.aboutTitle),
  eyebrow: intl.formatMessage(messages.aboutMeta),
  title: intl.formatMessage(messages.aboutTitle),
  lead: intl.formatMessage(messages.aboutLead),
  referenceLabel: intl.formatMessage(messages.commonUpdatedLabel),
  referenceValue: intl.formatMessage(messages.commonUpdatedValue),
  sections: [
    {
      title: intl.formatMessage(messages.aboutSectionMissionTitle),
      paragraphs: [intl.formatMessage(messages.aboutSectionMissionBody)],
    },
    {
      title: intl.formatMessage(messages.aboutSectionApproachTitle),
      paragraphs: [intl.formatMessage(messages.aboutSectionApproachBody)],
    },
    {
      title: intl.formatMessage(messages.aboutSectionAudienceTitle),
      paragraphs: [intl.formatMessage(messages.aboutSectionAudienceBody)],
    },
  ],
  asideCards: getCommonAsideCards(intl),
});

export const getFaqPageContent = (intl: IntlShape): StaticPageContent => ({
  pageTitle: intl.formatMessage(messages.faqTitle),
  eyebrow: intl.formatMessage(messages.faqMeta),
  title: intl.formatMessage(messages.faqTitle),
  lead: intl.formatMessage(messages.faqLead),
  referenceLabel: intl.formatMessage(messages.commonUpdatedLabel),
  referenceValue: intl.formatMessage(messages.commonUpdatedValue),
  sections: [
    {
      title: intl.formatMessage(messages.faqQuestionAccountTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionAccountAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionEmailConfirmationTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionEmailConfirmationAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionPasswordResetTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionPasswordResetAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionAndroidTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionAndroidAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionLowBandwidthTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionLowBandwidthAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionOfflineTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionOfflineAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionDataUsageTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionDataUsageAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionPricingTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionPricingAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionEnrollmentTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionEnrollmentAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionPacingTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionPacingAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionCertificatesTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionCertificatesAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionProgressTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionProgressAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionVideoIssueTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionVideoIssueAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionQuizIssueTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionQuizIssueAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionDataTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionDataAnswer)],
    },
    {
      title: intl.formatMessage(messages.faqQuestionDeletionTitle),
      paragraphs: [intl.formatMessage(messages.faqQuestionDeletionAnswer)],
    },
  ],
  asideCards: getCommonAsideCards(intl),
});

export const getHelpPageContent = (intl: IntlShape): StaticPageContent => ({
  pageTitle: intl.formatMessage(messages.helpTitle),
  eyebrow: intl.formatMessage(messages.helpMeta),
  title: intl.formatMessage(messages.helpTitle),
  lead: intl.formatMessage(messages.helpLead),
  referenceLabel: intl.formatMessage(messages.commonUpdatedLabel),
  referenceValue: intl.formatMessage(messages.commonUpdatedValue),
  sections: [
    {
      title: intl.formatMessage(messages.helpSectionEnrollmentTitle),
      paragraphs: [intl.formatMessage(messages.helpSectionEnrollmentBody)],
    },
    {
      title: intl.formatMessage(messages.helpSectionAccountTitle),
      paragraphs: [intl.formatMessage(messages.helpSectionAccountBody)],
    },
    {
      title: intl.formatMessage(messages.helpSectionLearningTitle),
      paragraphs: [intl.formatMessage(messages.helpSectionLearningBody)],
    },
  ],
  asideCards: [
    {
      title: intl.formatMessage(messages.helpAsideTitle),
      description: `${intl.formatMessage(messages.helpAsideBodyOne)} ${intl.formatMessage(messages.helpAsideBodyTwo)}`,
      links: [
        { label: intl.formatMessage(messages.commonContactButton), href: getSupportHref() },
      ],
    },
    ...getCommonAsideCards(intl).slice(0, 1),
  ],
});

export const getTermsPageContent = (intl: IntlShape): StaticPageContent => ({
  pageTitle: intl.formatMessage(messages.termsTitle),
  eyebrow: intl.formatMessage(messages.termsMeta),
  title: intl.formatMessage(messages.termsTitle),
  lead: intl.formatMessage(messages.termsLead),
  referenceLabel: intl.formatMessage(messages.commonUpdatedLabel),
  referenceValue: intl.formatMessage(messages.commonUpdatedValue),
  sections: [
    {
      title: intl.formatMessage(messages.termsSectionAcceptanceTitle),
      paragraphs: [intl.formatMessage(messages.termsSectionAcceptanceBody)],
    },
    {
      title: intl.formatMessage(messages.termsSectionAccountTitle),
      paragraphs: [intl.formatMessage(messages.termsSectionAccountBody)],
    },
    {
      title: intl.formatMessage(messages.termsSectionUsageTitle),
      paragraphs: [intl.formatMessage(messages.termsSectionUsageBody)],
    },
    {
      title: intl.formatMessage(messages.termsSectionAvailabilityTitle),
      paragraphs: [intl.formatMessage(messages.termsSectionAvailabilityBody)],
    },
    {
      title: intl.formatMessage(messages.termsSectionChangesTitle),
      paragraphs: [intl.formatMessage(messages.termsSectionChangesBody)],
    },
  ],
  asideCards: getCommonAsideCards(intl),
});

export const getPrivacyPageContent = (intl: IntlShape): StaticPageContent => ({
  pageTitle: intl.formatMessage(messages.privacyTitle),
  eyebrow: intl.formatMessage(messages.privacyMeta),
  title: intl.formatMessage(messages.privacyTitle),
  lead: intl.formatMessage(messages.privacyLead),
  referenceLabel: intl.formatMessage(messages.commonUpdatedLabel),
  referenceValue: intl.formatMessage(messages.commonUpdatedValue),
  sections: [
    {
      title: intl.formatMessage(messages.privacySectionHostingTitle),
      paragraphs: [intl.formatMessage(messages.privacySectionHostingBody)],
    },
    {
      title: intl.formatMessage(messages.privacySectionCollectionTitle),
      paragraphs: [intl.formatMessage(messages.privacySectionCollectionBody)],
    },
    {
      title: intl.formatMessage(messages.privacySectionUseTitle),
      paragraphs: [intl.formatMessage(messages.privacySectionUseBody)],
    },
    {
      title: intl.formatMessage(messages.privacySectionAnalyticsTitle),
      paragraphs: [intl.formatMessage(messages.privacySectionAnalyticsBody)],
    },
    {
      title: intl.formatMessage(messages.privacySectionDeletionTitle),
      paragraphs: [intl.formatMessage(messages.privacySectionDeletionBody)],
    },
    {
      title: intl.formatMessage(messages.privacySectionRightsTitle),
      paragraphs: [intl.formatMessage(messages.privacySectionRightsBody)],
    },
  ],
  asideCards: [
    {
      title: intl.formatMessage(messages.commonContactTitle),
      description: intl.formatMessage(messages.commonContactDescription),
      links: [
        { label: intl.formatMessage(messages.commonContactButton), href: getInfoEmailHref() },
      ],
    },
    {
      title: intl.formatMessage(messages.commonQuickLinksTitle),
      description: '',
      links: [
        { label: intl.formatMessage(messages.commonQuickLinksFaq), to: ROUTES.FAQ },
        { label: intl.formatMessage(messages.commonQuickLinksHelp), to: ROUTES.HELP },
      ],
    },
  ],
});
